import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Sparkles, Check, Loader2, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { submitInterest } from '../services/api';
import { useWorkspace } from '../context/WorkspaceContext';
import { useAuth } from '../context/AuthContext';

const PRO_FEATURES = [
  'Unlimited LINE channels',
  'Unlimited team members',
  'Priority support',
  'Early access to new features',
];

export default function ProDialog() {
  const { proDialogOpen, closeProDialog, selectedWorkspace, usage, limits } = useWorkspace();
  const { user } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const handleRequest = async () => {
    setSubmitting(true);
    try {
      await submitInterest({
        name: user?.name || 'Unknown',
        email: user?.email,
        company: selectedWorkspace?.name || '',
        channels: 'pro-request',
        message: `Pro upgrade request from workspace "${selectedWorkspace?.name}" (${selectedWorkspace?.id}); using ${usage.channels}/${limits.maxChannels ?? '∞'} channels.`,
      });
      setSent(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send the request. Email hello@linerichmenu.com instead.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={proDialogOpen} onOpenChange={(open) => { if (!open) { closeProDialog(); setSent(false); } }}>
      <DialogContent className="bg-[#18181B] border-[#27272A] max-w-[90vw] sm:max-w-md" data-testid="pro-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#00E05E]" />Upgrade to Pro
          </DialogTitle>
          <DialogDescription className="text-[#A1A1AA]">
            The Free plan includes {limits.maxChannels ?? 2} LINE channels per workspace. Pro removes the limits.
          </DialogDescription>
        </DialogHeader>
        {sent ? (
          <div className="py-6 text-center space-y-2" data-testid="pro-request-sent">
            <Check className="w-8 h-8 text-[#00E05E] mx-auto" />
            <p className="font-medium">Request received</p>
            <p className="text-sm text-[#A1A1AA]">We'll be in touch within 1 business day.</p>
          </div>
        ) : (
          <ul className="space-y-2 py-2">
            {PRO_FEATURES.map((f) => (
              <li key={f} className="flex items-center gap-2 text-sm">
                <Check className="w-4 h-4 text-[#00E05E] flex-shrink-0" />{f}
              </li>
            ))}
          </ul>
        )}
        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button asChild variant="outline" className="border-[#27272A]">
            <a href="mailto:hello@linerichmenu.com?subject=Pro%20upgrade"><Mail className="w-4 h-4 mr-2" />Email us</a>
          </Button>
          {!sent && (
            <Button onClick={handleRequest} disabled={submitting} className="btn-primary" data-testid="request-pro-btn">
              {submitting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending…</>) : 'Request Pro'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
